import { ref, onBeforeUnmount } from "vue";
import { PaymentStatus, type PayLedgerStatusResponse } from "~/types/payments";

/**
 * Polls the public `/payments/pay-ledger-status/{payment_intent_id}` endpoint
 * until a Stripe payment settles to a terminal state, or the budget is hit.
 *
 * 10 attempts at 1.5 s = 15 s budget. Token-cancellable to survive component
 * unmounts and to let the parent state machine bail if the user navigates
 * back.
 *
 * Public endpoint — no JWT required. The Stripe `payment_intent_id` is the
 * capability (24 random chars). Same composable used by both guest and
 * authenticated flows; auth state simply isn't relevant here.
 */

const DEFAULT_MAX_ATTEMPTS = 10;
const DEFAULT_DELAY_MS = 1500;

export type PayLedgerPollResult = "succeeded" | "failed" | "timeout" | "cancelled";

export interface PayLedgerPollOptions {
  paymentIntentId: string;
  maxAttempts?: number;
  delayMs?: number;
}

const TERMINAL_FAILED_STATUSES: PaymentStatus[] = [
  PaymentStatus.FAILED,
  PaymentStatus.CANCELED,
];

export function usePayLedgerPolling() {
  const config = useRuntimeConfig();
  const pollToken = ref(0);

  async function pollPayLedgerStatus(
    opts: PayLedgerPollOptions,
  ): Promise<PayLedgerPollResult> {
    const myToken = ++pollToken.value;
    const maxAttempts = opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    const delayMs = opts.delayMs ?? DEFAULT_DELAY_MS;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (myToken !== pollToken.value) return "cancelled";

      let res: PayLedgerStatusResponse;
      try {
        res = await $fetch<PayLedgerStatusResponse>(
          `${config.public.apiBase}/api/public/orders/payments/pay-ledger-status/${opts.paymentIntentId}`,
        );
      } catch {
        // Transient fetch error — wait and retry within the budget.
        await wait(delayMs);
        continue;
      }

      if (myToken !== pollToken.value) return "cancelled";

      const status = res.payment_status;
      if (status === PaymentStatus.SUCCEEDED) return "succeeded";
      if (status && TERMINAL_FAILED_STATUSES.includes(status)) return "failed";
      // PENDING / REQUIRES_ACTION / PARTIALLY_FUNDED / null all mean "still
      // settling" from the polling caller's perspective — keep waiting.

      await wait(delayMs);
    }

    return "timeout";
  }

  function cancelPayLedgerPolling(): void {
    pollToken.value++;
  }

  onBeforeUnmount(() => {
    cancelPayLedgerPolling();
  });

  return { pollPayLedgerStatus, cancelPayLedgerPolling };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
